import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import axios from 'axios';
import DemoAcceleratorCard from '../../components/common/DemoAcceleratorCard';
import SEO from '../../components/common/SEO';
import { buildCanonicalUrl } from '../../utils/seoUtils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const DemoLinksPage = () => {
  const [demoLinks, setDemoLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load demo links from API
  useEffect(() => {
    fetchDemoLinks();
  }, []);

  const fetchDemoLinks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/accelerators`);
      // Filter accelerators that have demo_url and keep full accelerator data
      const accelerators = response.data || [];
      const demoLinks = accelerators.filter(acc => acc.demo_url && acc.demo_url.trim() !== '');
      setDemoLinks(demoLinks);
    } catch (error) {
      console.error('Error loading demo links:', error);
      setDemoLinks([]);
    } finally {
      setLoading(false);
    }
  };

  // Group accelerators by tech_category (fallback to category_name if tech_category not set)
  const groupedLinks = demoLinks.reduce((groups, accelerator) => {
    const category = accelerator.tech_category || accelerator.category_name || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(accelerator);
    return groups;
  }, {});

  // Handle accelerator card click to open demo URL
  const handleAcceleratorClick = (accelerator) => {
    if (accelerator.demo_url) {
      window.open(accelerator.demo_url, '_blank', 'noopener,noreferrer');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO
          title="Accelerator Links"
          description="Access live demo links for KL Accelerators. Explore interactive demonstrations of our ready-to-deploy enterprise solutions."
          canonicalUrl={buildCanonicalUrl('/demo-links')}
          keywords="accelerator demos, live demos, enterprise accelerator links"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading accelerator links...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Accelerator Links"
        description="Access live demo links for KL Accelerators. Explore interactive demonstrations of our ready-to-deploy enterprise solutions organized by technology category."
        canonicalUrl={buildCanonicalUrl('/demo-links')}
        keywords="accelerator demos, live demos, enterprise accelerator links"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Accelerator Links
          </h1>
        </div>

        {/* Demo Links Content */}
        {demoLinks.length === 0 ? (
          <div className="text-center py-12">
            <ExternalLink className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No accelerator links available</h3>
            <p className="text-gray-500">
              Accelerator links will appear here once they are configured.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedLinks).map(([category, accelerators]) => (
              <div key={category} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-3">
                  {category} ({accelerators.length})
                </h2>
                {/* Grid layout - responsive grid that accommodates 6+ cards horizontally */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                  {accelerators.map((accelerator) => (
                     <DemoAcceleratorCard
                       key={accelerator.id}
                       accelerator={accelerator}
                       onClick={handleAcceleratorClick}
                     />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoLinksPage;